import React from 'react';
import {
  Dialog as DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from './primitives/dialog';
import cn from './primitives/cn';
import Button from './Button';
import CloseButton from './CloseButton';

/**
 * The CMS dialog.
 *
 * The API has not changed: `title`, `onClose`, `onConfirm`, `confirmLabel`, `cancelLabel`,
 * `confirmDisabled`, `busy`, `dismissOnBackdrop`, `size`, `id`, `color`, `className`,
 * `bodyClassName`, `footer`. All 13 call sites keep working untouched. Underneath,
 * react-bootstrap's Modal has been replaced by Radix.
 *
 * The props were named after behaviour rather than after either library precisely so this
 * swap would not reach the call sites, and it did not.
 *
 * **What Radix does that react-bootstrap did not.** Focus is trapped inside the dialog and
 * returned to whatever opened it on close; the rest of the page is marked `aria-hidden`
 * while it is open; and the title is wired to `aria-labelledby` automatically. Escape and
 * backdrop clicks behaved the same before and behave the same now.
 *
 * **`busy` is the one that has teeth.** The course upload holds this dialog open for up to
 * 300 seconds, and a dismissal mid-upload leaves a half-written course. Under
 * react-bootstrap that was enforced by withholding `onHide`, which stopped the × and the
 * Cancel button but not Escape. Radix fires one `onOpenChange` for every route, so `busy`
 * now genuinely blocks all of them.
 *
 * **`confirmDisabledReason`** (optional, SURF-4889) is why the confirm is off, shown in a
 * tooltip on it, as every Save in the CMS says what is missing (shared/ui/Button's
 * `disabledReason`); `onConfirmDisabledClick` is what a click on it does meanwhile. Pass ''
 * while there is nothing missing, so the button keeps its element. While `busy` the
 * confirm is plainly disabled, as before.
 *
 * The legacy `module`/`modal-*` classes are still emitted as hooks, for the same reason as
 * in `Card`: `.module .modal-header button.close` and `.modal-body .row` reach in from
 * main.css, and a tenant stylesheet may too.
 */
const SIZE_CLASS = {
  sm: 'tw-max-w-dialog-sm',
  lg: 'tw-max-w-dialog-lg',
  xl: 'tw-max-w-dialog-xl',
};

const Dialog = ({
  title,
  onClose,
  onConfirm,
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  confirmDisabled = false,
  confirmDisabledReason,
  onConfirmDisabledClick,
  busy = false,
  dismissOnBackdrop = false,
  size,
  id,
  color,
  className,
  bodyClassName = 'modal-body module-body',
  footer,
  children,
}) => {
  // Callers mount this conditionally, so it is open whenever it exists. Every dismissal
  // route arrives here as `open === false`.
  const handleOpenChange = (open) => {
    if (!open && !busy && onClose) onClose();
  };
  const block = (event) => event.preventDefault();

  return (
    <DialogRoot open onOpenChange={handleOpenChange}>
      <DialogContent
        id={id}
        showClose={false}
        /* `module` is a positioning hook, not a surface. main.css reaches in through it
           for the close button and the header, so the class has to stay — but `.module`
           also carries a border, a 12px radius and a shadow, and the dialog already has
           its own 16px radius and a much larger shadow. Both applied, so every dialog
           drew a second, tighter outline just inside its own edge. The utilities win over
           the stylesheet because `important: true`, and tailwind-merge drops the radius
           and shadow the dialog itself sets rather than fighting them. */
        className={cn(
          'module ng-animate-enabled tw-border-0',
          color,
          SIZE_CLASS[size],
          'tw-p-0',
          className
        )}
        onEscapeKeyDown={busy ? block : undefined}
        onPointerDownOutside={busy || !dismissOnBackdrop ? block : undefined}
        onInteractOutside={busy || !dismissOnBackdrop ? block : undefined}
      >
        <DialogHeader className="modal-header module-header">
          <DialogTitle className="modal-title tw-flex-1">{title}</DialogTitle>
          <CloseButton disabled={busy} onClick={onClose} />
        </DialogHeader>

        <DialogBody className={bodyClassName}>{children}</DialogBody>

        {footer !== null && (
          <DialogFooter className="modal-footer">
            {footer || (
              <>
                {onConfirm && (
                  <Button
                    variant="primary"
                    disabled={confirmDisabled || busy}
                    disabledReason={busy ? '' : confirmDisabledReason}
                    onDisabledClick={onConfirmDisabledClick}
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </Button>
                )}
                <Button variant="secondary" disabled={busy} onClick={onClose}>
                  {cancelLabel}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </DialogRoot>
  );
};

export default Dialog;
